/**
 * raffle.js — Product page + Entries page
 */

const pad = n => String(Math.max(0, n)).padStart(2, '0');

function formatMoney(cents, currency = 'GBP') {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency', currency, minimumFractionDigits: 2,
  }).format(cents / 100);
}

/* ══════════════════════════════════════════════════════════════
   PRODUCT PAGE
══════════════════════════════════════════════════════════════ */

async function initRaffleProduct() {
  const wrap = document.querySelector('.rp-wrap');
  if (!wrap) return;

  // All values come from server-rendered data attributes — no async fetch needed
  const endMs        = parseInt(wrap.dataset.endMs, 10);
  const totalTickets = parseInt(wrap.dataset.totalTickets, 10);
  const correctAns   = parseInt(wrap.dataset.correctAnswer, 10);
  const priceCents   = parseInt(wrap.dataset.priceCents, 10);
  const currency     = wrap.dataset.currency || 'GBP';
  const remaining    = parseInt(wrap.dataset.remaining, 10) || totalTickets;
  const soldOut      = wrap.dataset.soldOut === 'true';
  const entriesUrl   = wrap.dataset.entriesUrl || '';

  let maxQty = remaining;

  // Elements
  const $  = id => document.getElementById(id);
  const cdDays    = $('cd-days');
  const cdHours   = $('cd-hours');
  const cdMinutes = $('cd-minutes');
  const cdSeconds = $('cd-seconds');
  const closedMsg = $('rp-closed-msg');
  const statusBadge = $('rp-status-badge');
  const slider    = $('rp-slider');
  const tooltip   = $('rp-tooltip');
  const minusBtn  = $('rp-minus');
  const plusBtn   = $('rp-plus');
  const rpQty     = $('rp-qty');
  const ctaBtn    = $('rp-cta-btn');
  const ctaPrice  = $('rp-cta-price');
  const rpForm    = $('rp-form');
  const answerErr  = $('rp-answer-error');
  const progressFill = $('rp-progress-fill');
  const progressPct  = $('rp-progress-pct');
  const soldEl       = $('rp-sold');
  const remainingEl  = $('rp-remaining');

  let qty = 1;
  let raffleExpired = false;
  let answeredCorrectly = isNaN(correctAns);
  let selectedAnswer = null;

  // ── Finished state ─────────────────────────────────────────
  function setFinished(label) {
    // Update badge
    if (statusBadge) {
      statusBadge.textContent = 'Finished';
      statusBadge.classList.add('finished');
    }

    // Change "Competition ends on: DATE" → "Competition ended"
    const endDateEl = document.getElementById('rp-end-date');
    if (endDateEl) endDateEl.innerHTML = '<strong>Competition ended</strong>';

    // Hide: countdown, postal button, modal, progress, question, slider, form
    [
      document.getElementById('rp-countdown'),
      wrap.querySelector('.rp-postal-btn'),
      document.getElementById('rp-postal-modal'),
      wrap.querySelector('.rp-progress-wrap'),
      wrap.querySelector('.rp-question-section'),
      wrap.querySelector('.rp-selector-wrap'),
      document.getElementById('rp-form'),
    ].forEach(el => { if (el) el.style.display = 'none'; });

    // Show Search Entry List button
    const entriesBtn = document.getElementById('rp-entries-btn');
    if (entriesBtn) {
      entriesBtn.style.display = 'block';
      if (entriesUrl) entriesBtn.href = entriesUrl;
    }

    raffleExpired = true;
  }

  // ── Disable raffle (alias for setFinished) ─────────────────
  function disableRaffle(label) { setFinished(label); }

  if (soldOut || remaining === 0) setFinished('Sold Out');

  // ── Image gallery with arrow navigation ───────────────────
  const hero      = document.getElementById('rp-hero');
  const thumbBtns = [...document.querySelectorAll('.rp-thumb')];
  const prevBtn   = document.getElementById('rp-arrow-prev');
  const nextBtn   = document.getElementById('rp-arrow-next');
  let currentImg  = 0;

  function goToImage(index) {
    if (!thumbBtns.length) return;
    currentImg = (index + thumbBtns.length) % thumbBtns.length;
    const btn = thumbBtns[currentImg];
    if (hero) hero.src = btn.dataset.src;
    thumbBtns.forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
  }

  thumbBtns.forEach((btn, i) => {
    btn.addEventListener('click', () => goToImage(i));
  });
  if (prevBtn) prevBtn.addEventListener('click', () => goToImage(currentImg - 1));
  if (nextBtn) nextBtn.addEventListener('click', () => goToImage(currentImg + 1));

  // ── Countdown ──────────────────────────────────────────────
  function tick() {
    const diff = endMs - Date.now();
    if (diff <= 0 || isNaN(diff)) {
      ['00','00','00','00'].forEach((v, i) => {
        [cdDays, cdHours, cdMinutes, cdSeconds][i].textContent = v;
      });
      if (closedMsg) closedMsg.style.display = 'block';
      setFinished('Competition Closed');
      return;
    }
    const s = Math.floor(diff / 1000);
    if (cdDays)    cdDays.textContent    = pad(Math.floor(s / 86400));
    if (cdHours)   cdHours.textContent   = pad(Math.floor((s % 86400) / 3600));
    if (cdMinutes) cdMinutes.textContent = pad(Math.floor((s % 3600) / 60));
    if (cdSeconds) cdSeconds.textContent = pad(s % 60);
    setTimeout(tick, 1000);
  }
  if (!isNaN(endMs)) tick();

  // ── Qty + slider + tooltip ─────────────────────────────────
  function updateTooltip() {
    if (!slider || !tooltip) return;
    const min    = parseInt(slider.min, 10) || 1;
    const max    = parseInt(slider.max, 10) || totalTickets;
    const pct    = (qty - min) / Math.max(max - min, 1);
    const trackW = slider.offsetWidth;
    const thumbW = 20;
    const pos    = pct * (trackW - thumbW) + thumbW / 2;
    // Offset from left edge of the slider-container (accounts for the adj button width)
    const btnWidth = 44 + 12; // button width + gap
    tooltip.style.left = `${btnWidth + pos}px`;
    tooltip.style.transform = 'translateX(-50%)';
    tooltip.textContent = `${qty} Ticket${qty !== 1 ? 's' : ''}`;
  }

  function setQty(n) {
    qty = Math.max(1, Math.min(n, maxQty));
    if (slider)   slider.value   = qty;
    if (rpQty)    rpQty.value    = qty;
    if (ctaPrice) ctaPrice.textContent = formatMoney(priceCents * qty, currency);
    updateTooltip();
  }

  if (slider) {
    slider.addEventListener('input', () => setQty(parseInt(slider.value, 10)));
    window.addEventListener('resize', updateTooltip);
  }
  if (minusBtn) minusBtn.addEventListener('click', () => setQty(qty - 1));
  if (plusBtn)  plusBtn.addEventListener('click',  () => setQty(qty + 1));
  setQty(1);
  setTimeout(updateTooltip, 100); // after layout

  // ── Answer buttons ─────────────────────────────────────────
  document.querySelectorAll('.rp-answer-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.dataset.index, 10);
      selectedAnswer = idx;

      // Reset all
      document.querySelectorAll('.rp-answer-btn').forEach(b => {
        b.classList.remove('selected', 'correct', 'wrong');
        b.setAttribute('aria-pressed', 'false');
      });

      if (idx === correctAns) {
        btn.classList.add('correct');
        answeredCorrectly = true;
        if (answerErr) answerErr.textContent = '';
      } else {
        btn.classList.add('wrong');
        answeredCorrectly = false;
        if (answerErr) answerErr.textContent = '✗ Incorrect — please try again.';
      }
      btn.setAttribute('aria-pressed', 'true');
    });
  });

  // ── Form guard ─────────────────────────────────────────────
  if (rpForm) {
    rpForm.addEventListener('submit', e => {
      if (raffleExpired) { e.preventDefault(); return; }

      const hasQuestion = document.querySelectorAll('.rp-answer-btn').length > 0;
      if (hasQuestion) {
        if (selectedAnswer === null) {
          e.preventDefault();
          if (answerErr) answerErr.textContent = '⚠ Please answer the question to enter.';
          return;
        }
        if (!answeredCorrectly) {
          e.preventDefault();
          if (answerErr) answerErr.textContent = '✗ Incorrect answer — please try again.';
          return;
        }
      }
    });
  }
}

/* ══════════════════════════════════════════════════════════════
   ENTRIES PAGE
══════════════════════════════════════════════════════════════ */

function initRaffleEntries() {
  const wrap = document.querySelector('.raffle-entries-wrap');
  if (!wrap) return;

  const SUPABASE_URL  = wrap.dataset.supabaseUrl;
  const SUPABASE_ANON = wrap.dataset.supabaseAnonKey;
  const PAGE_SIZE     = 50;

  if (!SUPABASE_URL || !SUPABASE_ANON) return;

  const tbody         = document.getElementById('entries-body');
  const searchInput   = document.getElementById('entries-search');
  const statTotal     = document.getElementById('stat-total');
  const paginationEl  = document.getElementById('entries-pagination');
  const backRow       = document.getElementById('re-back-row');
  const pageTitle     = document.getElementById('re-page-title');
  const pageSub       = document.getElementById('re-page-sub');

  // Read ?product=TITLE from URL
  const filterProduct = new URLSearchParams(window.location.search).get('product') || '';

  // If filtering by product, update the page header and show back button
  if (filterProduct) {
    if (pageTitle) pageTitle.textContent = filterProduct;
    if (pageSub)   pageSub.textContent   = `All ticket holders for this raffle.`;
    if (backRow)   backRow.style.display = 'block';
  }

  let allEntries = [], filteredEntries = [];
  let currentPage = 1, sortKey = 'purchased_at', sortAsc = false;

  async function fetchEntries() {
    setLoading(true);
    try {
      // If filtering by product, push the filter to Supabase for efficiency
      let url = `${SUPABASE_URL}/rest/v1/public_raffle_entries?select=ticket_id,customer_name,order_id,product_title,purchased_at&order=purchased_at.desc&limit=10000`;
      if (filterProduct) {
        url += `&product_title=eq.${encodeURIComponent(filterProduct)}`;
      }
      const res = await fetch(url, {
        headers: { 'apikey': SUPABASE_ANON, 'Authorization': `Bearer ${SUPABASE_ANON}` }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      allEntries = await res.json();
      applyFilters();
    } catch (err) {
      showError('Could not load entries. Please try again later.');
    } finally {
      setLoading(false);
    }
  }

  function setLoading(on) {
    const row = document.getElementById('entries-loading-row');
    if (on) { if (row) row.style.display = ''; } else { if (row) row.remove(); }
  }
  function showError(msg) {
    if (tbody) tbody.innerHTML = `<tr><td colspan="4" class="entries-state-cell entries-error">${msg}</td></tr>`;
  }

  function applyFilters() {
    const term = searchInput?.value.trim().toLowerCase() || '';
    filteredEntries = allEntries.filter(e => {
      if (term && !`${e.ticket_id} ${e.customer_name} ${e.order_id}`.toLowerCase().includes(term)) return false;
      return true;
    });
    filteredEntries.sort((a, b) => {
      let va = a[sortKey] ?? '', vb = b[sortKey] ?? '';
      if (typeof va === 'string') va = va.toLowerCase();
      if (typeof vb === 'string') vb = vb.toLowerCase();
      return sortAsc ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
    });
    if (statTotal) statTotal.textContent = filteredEntries.length.toLocaleString();
    currentPage = 1;
    renderPage();
  }

  function renderPage() {
    if (!tbody) return;
    const slice = filteredEntries.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
    if (!slice.length) {
      tbody.innerHTML = '<tr><td colspan="4" class="entries-state-cell">No entries found.</td></tr>';
      if (paginationEl) paginationEl.innerHTML = '';
      return;
    }
    tbody.innerHTML = slice.map(e => {
      const date = e.purchased_at
        ? new Date(e.purchased_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })
        : '—';
      return `<tr>
        <td class="td-ticket"><span class="ticket-badge">#${esc(e.ticket_id)}</span></td>
        <td class="td-name">${esc(e.customer_name)}</td>
        <td class="td-order">${esc(e.order_id)}</td>
        <td class="td-date">${date}</td>
      </tr>`;
    }).join('');
    renderPagination();
  }

  function renderPagination() {
    if (!paginationEl) return;
    const total = Math.ceil(filteredEntries.length / PAGE_SIZE);
    if (total <= 1) { paginationEl.innerHTML = ''; return; }
    let html = `<button class="page-btn" ${currentPage===1?'disabled':''} data-page="${currentPage-1}">← Prev</button>`;
    for (let p = 1; p <= total; p++) {
      if (total > 7 && Math.abs(p - currentPage) > 2 && p !== 1 && p !== total) {
        if (p === 2 || p === total - 1) html += '<span class="page-ellipsis">…</span>';
        continue;
      }
      html += `<button class="page-btn ${p===currentPage?'active':''}" data-page="${p}">${p}</button>`;
    }
    html += `<button class="page-btn" ${currentPage===total?'disabled':''} data-page="${currentPage+1}">Next →</button>`;
    paginationEl.innerHTML = html;
    paginationEl.querySelectorAll('.page-btn:not([disabled])').forEach(btn => {
      btn.addEventListener('click', () => {
        currentPage = parseInt(btn.dataset.page, 10);
        renderPage();
        wrap.scrollIntoView({ behavior:'smooth', block:'start' });
      });
    });
  }

  document.querySelectorAll('.raffle-table th[data-sort]').forEach(th => {
    th.style.cursor = 'pointer';
    th.addEventListener('click', () => {
      sortAsc = sortKey === th.dataset.sort ? !sortAsc : true;
      sortKey = th.dataset.sort;
      document.querySelectorAll('.raffle-table th[data-sort] .sort-icon').forEach(i => i.textContent = '⇅');
      th.querySelector('.sort-icon').textContent = sortAsc ? '↑' : '↓';
      applyFilters();
    });
  });

  if (searchInput) {
    let d; searchInput.addEventListener('input', () => { clearTimeout(d); d = setTimeout(applyFilters, 250); });
  }

  function esc(s) {
    return String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  fetchEntries();
}

/* ── Postal entry modal ──────────────────────────────────────── */

function initPostalModal() {
  const openBtn  = document.getElementById('rp-postal-btn');
  const modal    = document.getElementById('rp-postal-modal');
  const closeBtn = document.getElementById('rp-modal-close');

  if (!openBtn || !modal) return;

  function openModal() {
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
    closeBtn?.focus();
  }
  function closeModal() {
    modal.setAttribute('hidden', '');
    document.body.style.overflow = '';
    openBtn.focus();
  }

  openBtn.addEventListener('click', openModal);
  closeBtn?.addEventListener('click', closeModal);

  // Close on overlay click
  modal.addEventListener('click', e => {
    if (e.target === modal) closeModal();
  });

  // Close on Escape
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
  });
}

/* ── Product tabs ───────────────────────────────────────────── */

function initRaffleTabs() {
  const tabs   = document.querySelectorAll('.rp-tab');
  const panels = document.querySelectorAll('.rp-tab-panel');
  if (!tabs.length) return;

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      const target = tab.dataset.tab;

      tabs.forEach(t => {
        t.classList.remove('active');
        t.setAttribute('aria-selected', 'false');
      });
      panels.forEach(p => p.setAttribute('hidden', ''));

      tab.classList.add('active');
      tab.setAttribute('aria-selected', 'true');
      const panel = document.getElementById(`tab-${target}`);
      if (panel) panel.removeAttribute('hidden');
    });
  });
}

/* ── Init ───────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initRaffleProduct();
  initRaffleEntries();
  initRaffleTabs();
  initPostalModal();
});
